import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Stack,
  Box,
  Paper,
  IconButton,
  Divider,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  ForumOutlined as MessageIcon,
  PersonOutline as PersonIcon,
  PhoneOutlined as PhoneIcon,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";

import { copyTextToClipboard, openWhatsAppWeb } from "utils/registrationRequestMessaging";

const formatRecipientPhone = (phone) => {
  if (!phone) return "—";
  const digits = String(phone).replace(/\D/g, "");
  if (!digits) return phone;
  if (digits.length <= 4) return `+${digits}`;
  return `+${digits.slice(0, 2)} ${digits.slice(2)}`;
};

const RegistrationNotifyDialog = ({
  open,
  onClose,
  title = "Notify applicant",
  subtitle,
  recipientName,
  recipientPhone,
  recipientMeta,
  message = "",
  variant = "default",
  doneLabel = "Done",
  onDone,
  doneLoading = false,
  disableBackdropClose = false,
}) => {
  const theme = useTheme();

  const handleClose = (_, reason) => {
    if (disableBackdropClose && reason === "backdropClick") return;
    if (doneLoading) return;
    onClose?.();
  };

  const handleCopy = () => copyTextToClipboard(message);

  const handleWhatsApp = () => openWhatsAppWeb(recipientPhone, message);

  const handleDone = () => {
    if (onDone) onDone();
    else onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          boxShadow: theme.shadows[16],
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          pt: 2.5,
          pb: 2,
          pr: 6,
          position: "relative",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack spacing={1}>
          <Typography variant="h5" fontWeight={700} lineHeight={1.25}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          ) : null}
        </Stack>
        <IconButton
          size="small"
          onClick={handleClose}
          disabled={doneLoading}
          aria-label="Close"
          sx={{ position: "absolute", top: 12, right: 12 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Stack spacing={2}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 0.75,
              px: 1.25,
              py: 0.75,
              borderRadius: 1.5,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: theme.palette.mode === "dark" ? alpha(theme.palette.common.white, 0.04) : "grey.50",
              mt: 2,
            }}
          >
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mr: 0.25 }}>
              To:
            </Typography>
            <PersonIcon sx={{ fontSize: 14, color: "text.secondary" }} />
            <Typography variant="caption" fontWeight={600} noWrap>
              {recipientName?.trim() || "Applicant"}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              ·
            </Typography>
            <PhoneIcon sx={{ fontSize: 14, color: "text.secondary" }} />
            <Typography variant="caption" color="text.secondary" noWrap>
              {formatRecipientPhone(recipientPhone)}
            </Typography>
            {recipientMeta ? (
              <>
                <Typography variant="caption" color="text.disabled">
                  ·
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {recipientMeta}
                </Typography>
              </>
            ) : null}
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing={0.08}>
              Message preview
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                mt: 1,
                p: 2,
                borderRadius: 2,
                maxHeight: 220,
                overflow: "auto",
                bgcolor: "background.paper",
              }}
            >
              <Typography
                component="pre"
                variant="body2"
                sx={{
                  m: 0,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                  lineHeight: 1.6,
                  color: "text.primary",
                }}
              >
                {message}
              </Typography>
            </Paper>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Copy the message or open WhatsApp Web to send it to the applicant.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: 1.25,
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ flex: 1 }}>
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopy}
            disabled={!message?.trim() || doneLoading}
            fullWidth
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Copy message
          </Button>
          <Button
            variant="contained"
            startIcon={<MessageIcon />}
            onClick={handleWhatsApp}
            disabled={!message?.trim() || doneLoading}
            fullWidth
            sx={{
              textTransform: "none",
              fontWeight: 600,
              bgcolor: "#25D366",
              "&:hover": { bgcolor: "#1DA851" },
            }}
          >
            Open WhatsApp
          </Button>
        </Stack>
        <LoadingButton
          variant="contained"
          color="primary"
          onClick={handleDone}
          loading={doneLoading}
          sx={{ textTransform: "none", fontWeight: 600, minWidth: { sm: 120 } }}
        >
          {doneLabel}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default RegistrationNotifyDialog;
